import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yaml from 'js-yaml';
import { Button } from '@/components/Common/button';

interface ReferenceSection {
  id: string;
  key: string;
  body: string;
}

interface ReferenceTextsFormProps {
  initialContent?: string;
  onSave: (content: string) => void;
  onCancel: () => void;
}

function valueToBody(value: unknown): string {
  if (typeof value === 'string') return value;
  try {
    return yaml.dump(value, { indent: 2, lineWidth: -1, noRefs: true }).replace(/\n$/, '');
  } catch {
    return String(value);
  }
}

export function ReferenceTextsForm({ initialContent, onSave, onCancel }: ReferenceTextsFormProps) {
  const { t } = useTranslation();
  const [sections, setSections] = useState<ReferenceSection[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!initialContent?.trim()) {
      setSections([]);
      setExpanded(new Set());
      return;
    }
    try {
      const parsed = yaml.load(initialContent) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        const entries = Object.entries(parsed as Record<string, unknown>).map(([key, value], index) => ({
          id: `ref-${Date.now()}-${index}`,
          key,
          body: valueToBody(value),
        }));
        setSections(entries);
        setExpanded(new Set(entries.map((e) => e.id)));
      }
    } catch (error) {
      console.error('Error parsing reference_texts YAML:', error);
    }
  }, [initialContent]);

  const handleAdd = () => {
    const id = `ref-${Date.now()}`;
    const next: ReferenceSection = {
      id,
      key: '',
      body: '',
    };
    setSections([next, ...sections]);
    setExpanded(new Set([...expanded, id]));
  };

  const handleUpdate = (id: string, field: keyof Omit<ReferenceSection, 'id'>, value: string) => {
    setSections(sections.map((s) => (s.id === id ? { ...s, [field]: value } : s)));
  };

  const handleDelete = (id: string) => {
    setSections(sections.filter((s) => s.id !== id));
    const next = new Set(expanded);
    next.delete(id);
    setExpanded(next);
  };

  const toggleExpanded = (id: string) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
  };

  const handleSave = () => {
    const obj: Record<string, string> = {};
    for (const s of sections) {
      const k = s.key.trim();
      if (!k) continue;
      obj[k] = s.body;
    }
    const yamlContent = yaml.dump(obj, {
      indent: 2,
      lineWidth: -1,
      noRefs: true,
      sortKeys: false,
    });
    onSave(yamlContent);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center pb-4 border-b border-border">
        <div>
          <p className="text-sm text-muted-foreground">
            {t('admin.configForms.referenceTexts.intro')}
          </p>
          <p className="text-xs text-muted-foreground mt-1">{t('admin.configForms.referenceTexts.hint')}</p>
        </div>
        <Button type="button" onClick={handleAdd} size="sm" className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          {t('admin.configForms.referenceTexts.addSection')}
        </Button>
      </div>

      <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
        {sections.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {t('admin.configForms.referenceTexts.empty')}
          </div>
        ) : (
          sections.map((s) => {
            const isOpen = expanded.has(s.id);
            return (
              <div key={s.id} className="border border-border rounded-lg bg-muted/20 overflow-hidden">
                <div className="flex items-center gap-2 p-3 border-b border-border/80 bg-muted/30">
                  <button
                    type="button"
                    onClick={() => toggleExpanded(s.id)}
                    className="p-1 rounded-md hover:bg-muted text-muted-foreground"
                    aria-expanded={isOpen}
                  >
                    {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                  <input
                    type="text"
                    value={s.key}
                    onChange={(e) => handleUpdate(s.id, 'key', e.target.value)}
                    placeholder={t('admin.configForms.referenceTexts.keyPlaceholder')}
                    className="flex-1 min-w-0 px-2 py-1.5 text-sm bg-background border border-border rounded-md font-mono"
                  />
                  <Button type="button" variant="ghost" size="sm" onClick={() => handleDelete(s.id)} className="text-destructive">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                {isOpen && (
                  <div className="p-3">
                    <textarea
                      value={s.body}
                      onChange={(e) => handleUpdate(s.id, 'body', e.target.value)}
                      placeholder={t('admin.configForms.referenceTexts.bodyPlaceholder')}
                      className="w-full min-h-[200px] px-3 py-2 text-sm font-mono bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-y"
                    />
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <div className="flex gap-2 justify-end pt-4 border-t border-border">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('actions.cancel')}
        </Button>
        <Button type="button" onClick={handleSave}>
          {t('actions.save')}
        </Button>
      </div>
    </div>
  );
}
